import React, { useState, useEffect, useMemo } from "react";
import { Table, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { TableProps, ColumnsType } from "antd/es/table";
import "antd/dist/reset.css";
import ImageWithBasePath from "../../../../../../core/common/imageWithBasePath";
import CommonSelect from "../../../../../../core/common/commonSelect";
import { formatNumber } from "../../../helpers/numbers.helper";
import "../../../styles/charts/DailyLeadPerformanceChart.scss";

interface PerformerStageData {
    employee_id: number;
    employee_name: string;
    employee_profile: string | null;
    stages: Record<string, number>;
    total: number;
    key?: string;
}

interface PerformersStageWiseDataTableProps {
    data: any;
    loading?: boolean;
    error?: string;
    showPagination?: boolean;
    pageSize?: number;
    showSearch?: boolean;
    maxHeight?: number;
}

const isLightColor = (hexColor: string): boolean => {
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 2), 16);
    const b = parseInt(hex.substring(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.7;
};

const getContrastingColors = (baseColor: string) => {
    const isLight = isLightColor(baseColor);

    if (isLight) {
        return {
            backgroundColor: `${baseColor}80`,
            color: "#1f2937",
            border: `1px solid ${baseColor}`,
        };
    } else {
        return {
            backgroundColor: `${baseColor}20`,
            color: baseColor,
            border: `1px solid ${baseColor}40`,
        };
    }
};

const PerformersStageWiseDataTable: React.FC<
    PerformersStageWiseDataTableProps
> = ({
    data,
    loading = false,
    error,
    showPagination = true,
    pageSize = 10,
    showSearch = true,
    maxHeight = 280,
}) => {
        const [searchText, setSearchText] = useState("");
        const [filteredData, setFilteredData] = useState<PerformerStageData[]>([]);
        const [selectedView, setSelectedView] = useState<string>("total");

        // Determine current dataset (total or by_form)
        const currentData = useMemo(() => {
            if (selectedView === "total") {
                return data?.total || [];
            }
            // Find specific form data
            const formData = data?.by_form?.find(
                (form: any) => form.form_id.toString() === selectedView
            );
            return formData?.performers || [];
        }, [data, selectedView]);

        // Get all unique stages from the data
        const uniqueStages = useMemo(() => {
            const stagesSet = new Set<string>();

            currentData.forEach((item: PerformerStageData) => {
                if (item.stages) {
                    Object.keys(item.stages).forEach((stage) => stagesSet.add(stage));
                }
            });

            return Array.from(stagesSet);
        }, [currentData]);

        // Get stage colors from pipelines
        const getStageColor = (stageName: string) => {
            if (!data?.pipelines || data.pipelines.length === 0) return "#6366f1";

            const pipeline = data.pipelines[0];
            const stageIndex = pipeline.stages?.indexOf(stageName);

            if (stageIndex !== -1 && pipeline.stages_color) {
                return pipeline.stages_color[stageIndex];
            }
            return "#6366f1";
        };

        const addKeysToData = (
            dataArray: PerformerStageData[]
        ): PerformerStageData[] => {
            return dataArray.map((item, index) => ({
                ...item,
                key: `${item.employee_id}-${index}`,
            }));
        };

        const processedData = useMemo(() => {
            return addKeysToData(currentData);
        }, [currentData]);

        const handleSearch = (value: string) => {
            setSearchText(value);
            if (!value) {
                setFilteredData(processedData);
                return;
            }

            const searchLower = value.toLowerCase();
            const filtered = processedData.filter((record) => {
                return (
                    record.employee_name?.toLowerCase().includes(searchLower) ||
                    record.employee_id?.toString().includes(searchLower)
                );
            });

            setFilteredData(filtered);
        };

        useEffect(() => {
            setFilteredData(processedData);
            setSearchText("");
        }, [processedData]);

        if (error) {
            return (
                <div className="table-error-state">
                    <i className="ti ti-alert-circle text-danger" />
                    <p className="text-danger">Error loading data</p>
                    <small className="text-muted">{error}</small>
                </div>
            );
        }

        // Build dynamic columns
        const columns: ColumnsType<PerformerStageData> = [
            {
                title: "Employee",
                dataIndex: "employee_name",
                key: "employee_name",
                render: (text: string, record: PerformerStageData) => (
                    <div className="d-flex align-items-center file-name-icon">
                        <li className="avatar avatar-md avatar-rounded object-cover flex-shrink-0">
                            <ImageWithBasePath
                                src={
                                    !record?.employee_profile ||
                                        record?.employee_profile === "null"
                                        ? "assets/img/users/user.png"
                                        : record?.employee_profile
                                }
                                alt="img"
                            />
                        </li>
                        <div className="">
                            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">{text}</h6>
                        </div>
                    </div>
                ),
                sorter: (a, b) =>
                    (a?.employee_name || "").localeCompare(b?.employee_name || ""),
            },
            ...uniqueStages.map((stage) => ({
                title: stage,
                dataIndex: ["stages", stage],
                key: `stage_${stage}`,
                render: (value: number) => {
                    const baseColor = getStageColor(stage);
                    const { backgroundColor, color, border } = getContrastingColors(baseColor);

                    return (
                        <h6
                            className="badge"
                            style={{
                                backgroundColor,
                                color,
                                border,
                                fontWeight: 500,
                            }}
                        >
                            {formatNumber(value || 0)}
                        </h6>
                    );
                },
                sorter: (a: any, b: any) => (a.stages?.[stage] || 0) - (b.stages?.[stage] || 0),
                align: "center" as const,
                width: 100,
            })),
            {
                title: "Total",
                dataIndex: "total",
                key: "total",
                render: (text: number) => (
                    <h6 className="badge badge-primary">{formatNumber(text)}</h6>
                ),
                sorter: (a, b) => a.total - b.total,
                align: "center",
                width: 100,
            },
        ];

        const viewOptions = [
            { value: "total", label: "All Performers (Total)" },
            ...(data?.by_form?.map((form: any) => ({
                value: form.form_id.toString(),
                label: `${form.form_name} (${form.pipeline_name})`,
            })) || []),
        ];


        const selectedOption = viewOptions.find(opt => opt.value === selectedView) || viewOptions[0];

        const tableProps: TableProps<PerformerStageData> = {
            columns,
            dataSource: filteredData,
            loading,
            rowKey: "key",
            scroll: {
                y: maxHeight,
                x: "max-content",
            },
            pagination: showPagination
                ? {
                    pageSize,
                    showSizeChanger: false,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                }
                : false,
        };

        return (
            <div style={{ width: "100%" }}>
                <div className="table-controls d-flex justify-content-between align-items-center mb-3">
                    <div className="pipeline-filter" style={{ width: "200px" }}>
                        <CommonSelect
                            className="select small text-muteds"
                            options={viewOptions}
                            defaultValue={selectedOption}
                            onChange={(selected: any) => {
                                setSelectedView(selected.value);
                            }}
                            placeHolder="Filter By Form"
                        />
                    </div>

                    {showSearch && (
                        <div className="search-wrapper">
                            <Input
                                style={{ width: "160px" }}
                                className="p-2"
                                placeholder="Search employees..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                                allowClear
                            />
                            {searchText && (
                                <span style={{ marginLeft: 10, color: "#888", fontSize: 14 }}>
                                    {filteredData.length} of {processedData.length} records
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <Table {...tableProps} />
            </div>
        );
    };

export default PerformersStageWiseDataTable;
